/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Article} from "types/entities";
import {Driver} from "types/entities/Driver";
import {currencyNumberFormat, translate} from "utils/utility";
import clsx from "clsx";
import {ShoppingCartItem} from "types/entities/ShoppingCartItem";
import ButtonRegistration, {
    ButtonRegistrationStyle,
    ButtonRegistrationType,
} from "pages/Front/Registration/components/ButtonRegistration";
import {CheckIcon, CogIcon, TrashIcon} from "@heroicons/react/24/solid";
import {ChevronRightIcon, TicketIcon} from "@heroicons/react/24/outline";
import ChevronLeftIcon from "@heroicons/react/24/solid/ChevronLeftIcon";

interface ArticleRowProps {
    account: Driver;
    article: Article;
    driverShoppingCartItems: ShoppingCartItem[];
    isLoading: boolean;
    onSelect: (article: Article, shoppingCartItem: ShoppingCartItem | null, quantity: number) => void;
    onRemove?: (shoppingCartItem: ShoppingCartItem) => void;
    useQuantity?: boolean;
    useRemove?: boolean;
}

const ArticleRow = (props: ArticleRowProps) => {
    const {t} = useTranslation();
    const [quantity, setQuantity] = useState<number>(1);
    const [changeWithoutSaving, setChangeWithoutSaving] = useState<boolean>(false);
    // -1 || index
    const selectedArticleIndex = props.driverShoppingCartItems
        ? props.driverShoppingCartItems.findIndex(
              shoppingCartItem => shoppingCartItem.article_id === props.article.id && shoppingCartItem.driver_id === props.account.id
          )
        : -1;
    const selectedArticle: ShoppingCartItem | null = selectedArticleIndex !== -1 ? props.driverShoppingCartItems[selectedArticleIndex] : null;
    const isSelected = selectedArticleIndex !== -1;
    const selectedText = !changeWithoutSaving ? t("Selected") : t("Update");

    const quantityHandler = (qty: number) => {
        setQuantity(qty);
    };

    useEffect(() => {
        if (selectedArticle) {
            setQuantity(selectedArticle.quantity);
        }
    }, [selectedArticle]);

    useEffect(() => {
        if (selectedArticle?.quantity !== quantity) {
            setChangeWithoutSaving(true);
        } else {
            setChangeWithoutSaving(false);
        }
    }, [quantity, selectedArticle?.quantity]);

    return (
        <div
            className={clsx(
                "ArticleRow flex w-full group border-b border-dashed border-black/10 last:border-b-0 justify-between",
                props.useQuantity ? "flex-col" : "flex-row"
            )}
        >
            <div
                className={clsx(
                    "flex flex-row pr-2 justify-start text-sm leading-6 font-bold w-full",
                    props.useQuantity ? "pt-2.5 items-start text-left" : "py-2.5 items-center truncate flex-auto"
                )}
            >
                <div className={"mr-2"}>
                    <TicketIcon className={"min-w-5 min-h-5 max-h-5 max-w-5 w-5 h-5"} />
                </div>
                <div className={clsx(props.useQuantity && "text-base")}>{translate(props.article.article_name)}</div>
            </div>
            <div className={clsx("flex flex-row w-full", props.useQuantity && "justify-end")}>
                <div className={clsx("flex py-2.5 items-center flex-0", props.useQuantity ? "pr-2 w-full justify-start" : "px-2")}>
                    <span>{currencyNumberFormat(props.article.article_value)}</span>
                </div>
                {props.useQuantity && (
                    <div className={"flex py-2.5 min-w-[146px] px-2"}>
                        <div className={"flex flex-row group"}>
                            <button
                                className={"arrow-up active:text-gray-700 w-auto px-2 bg-black rounded-tl-xl rounded-bl-xl border border-black border-solid"}
                                type={"button"}
                                onClick={() => {
                                    quantityHandler(quantity > 1 ? quantity - 1 : 0);
                                }}
                            >
                                <ChevronLeftIcon className={"w-6 h-6 text-white"} />
                            </button>
                            <input
                                className={
                                    "flex w-[46px] text-center border-0 pb-3 pt-3 px-2 shadow-sm ring-0 border-r border-l border-gray-600 focus:outline-0 text-sm sm:leading-6 font-thin placeholder:text-gray-400 placeholder:font-thin"
                                }
                                min={1}
                                type="number"
                                value={quantity}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => quantityHandler(+e.target.value)}
                            />
                            <button
                                className={"arrow-up active:text-green-500 w-auto px-2 bg-black rounded-tr-xl rounded-br-xl border border-black border-solid "}
                                type={"button"}
                                onClick={() => {
                                    quantityHandler(quantity < 15 ? quantity + 1 : quantity);
                                }}
                            >
                                <ChevronRightIcon className={"w-6 h-6 text-white"} />
                            </button>
                        </div>
                    </div>
                )}
                <div className={"flex py-2.5 flex-row justify-end group-last:last:rounded-br-lg min-w-[254px] pl-2"}>
                    <ButtonRegistration
                        className={"max-w-[12rem] w-full justify-between shadow"}
                        disabled={props.isLoading}
                        loading={props.isLoading}
                        style={
                            isSelected && !changeWithoutSaving
                                ? ButtonRegistrationStyle.Green
                                : isSelected && changeWithoutSaving
                                  ? ButtonRegistrationStyle.Orange
                                  : ButtonRegistrationStyle.Black
                        }
                        type={ButtonRegistrationType.Button}
                        onClick={() => props.onSelect(props.article, selectedArticle, quantity)}
                    >
                        {!props.isLoading && (
                            <span className={"w-5 h-5 bg-white border border-black/10 shadow rounded-full flex justify-center items-center"}>
                                {!isSelected && <></>}
                                {isSelected && changeWithoutSaving && <CogIcon className={"w-4 h-4 text-orange-400"} />}
                                {isSelected && !changeWithoutSaving && <CheckIcon className={"w-4 h-4 text-green-500"} />}
                            </span>
                        )}
                        <span className={"ml-2"}>{isSelected ? selectedText : t("Select")}</span>
                    </ButtonRegistration>
                    {props.useRemove && (
                        <button
                            className={clsx(
                                "ml-4 p-2 px-3 rounded-xl shadow border  border-solid text-white",
                                !isSelected ? "bg-red-500/20 border-black/10" : "bg-red-500 border-black/20"
                            )}
                            disabled={!isSelected}
                            onClick={() => props.onRemove && selectedArticle && props.onRemove(selectedArticle)}
                        >
                            <span className={"flex justify-center items-center"}>
                                <TrashIcon className={"w-5 h-5 text-white"} />
                            </span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ArticleRow;
